import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Skeleton, Typography, CircularProgress, useTheme,
} from '@mui/material';
import {
  CalendarToday as CalendarIcon,
  Email as EmailIcon,
  Refresh as RefreshIcon,
  Check as CheckIcon,
} from '@mui/icons-material';
import { useOutletContext } from 'react-router-dom';
import GoogleAuthButton from './GoogleAuthButton';
import SectionCard from './settings/SectionCard';
import StatusPill from './settings/StatusPill';
import { useAuth } from '../contexts/AuthContext';
import config from '../config';
import type { LayoutContext } from '../App';

type ServiceStatus = 'connected' | 'disconnected';

interface GoogleIntegrationCardProps {
  // false while another primary-emphasis button is on screen, so the connect button drops to secondary
  allowPrimary?: boolean;
  onGoogleEmailChange?: (email: string | null) => void;
  onAnnounce?: (message: string) => void;
}

const ServiceRow: React.FC<{
  icon: React.ReactNode; name: string; desc: string; status: ServiceStatus; reconnect: React.ReactNode;
}> = ({ icon, name, desc, status, reconnect }) => {
  const { palette } = useTheme();
  const t = palette.tokens;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1.5, flexWrap: 'wrap' }}>
      <Box sx={{ width: 40, height: 40, borderRadius: '10px', bgcolor: t.surfaceSoft, color: t.primary, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }} aria-hidden>
        {icon}
      </Box>
      <Box sx={{ flex: 1, minWidth: 140 }}>
        <Typography sx={{ fontSize: '16px', fontWeight: 600, color: t.ink }}>{name}</Typography>
        <Typography sx={{ fontSize: '14px', color: t.muted }}>{desc}</Typography>
      </Box>
      {status === 'connected' ? (
        <StatusPill tone="success" label="Connected" />
      ) : (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <StatusPill tone="warning" label="Needs attention" />
          {reconnect}
        </Box>
      )}
    </Box>
  );
};

const GoogleIntegrationCard: React.FC<GoogleIntegrationCardProps> = ({ allowPrimary = true, onGoogleEmailChange, onAnnounce }) => {
  const { palette } = useTheme();
  const t = palette.tokens;
  const { user, token: authToken } = useAuth();
  const { refreshCopilotData } = useOutletContext<LayoutContext>();
  const [initialLoad, setInitialLoad] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshed, setRefreshed] = useState(false);
  const [calendar, setCalendar] = useState<ServiceStatus>('disconnected');
  const [gmail, setGmail] = useState<ServiceStatus>('disconnected');
  const [googleEmail, setGoogleEmail] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [disconnectError, setDisconnectError] = useState<string | null>(null);

  const applyEmail = useCallback((email: string | null) => {
    setGoogleEmail(email);
    onGoogleEmailChange?.(email);
  }, [onGoogleEmailChange]);

  const checkStatus = useCallback(async () => {
    const token = authToken || localStorage.getItem('auth_token');
    const none = () => { setCalendar('disconnected'); setGmail('disconnected'); applyEmail(null); };
    if (!token) { none(); return; }
    try {
      // Refreshes the Google token first so the scopes check below reflects the live state
      try {
        await fetch(`${config.apiUrl}/api/auth/google/refresh`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
      } catch {}
      const response = await fetch(`${config.apiUrl}/api/auth/google/scopes`, { headers: { Authorization: `Bearer ${token}` } });
      const data = response.ok ? await response.json() : null;
      if (data?.success && data.is_authenticated) {
        setCalendar(data.services?.calendar ? 'connected' : 'disconnected');
        setGmail(data.services?.gmail ? 'connected' : 'disconnected');
        applyEmail(data.google_email || null);
      } else {
        none();
      }
    } catch {
      none();
    }
  }, [authToken, applyEmail]);

  useEffect(() => {
    if (!user) return;
    checkStatus().finally(() => setInitialLoad(false));
  }, [user, checkStatus]);

  useEffect(() => {
    const handleFocus = () => { if (user) checkStatus(); };
    const handleAuthSuccess = () => { setTimeout(checkStatus, 1000); refreshCopilotData(); };
    window.addEventListener('focus', handleFocus);
    window.addEventListener('googleAuthSuccess', handleAuthSuccess as EventListener);
    return () => {
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('googleAuthSuccess', handleAuthSuccess as EventListener);
    };
  }, [user, checkStatus, refreshCopilotData]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await checkStatus();
    setRefreshing(false);
    setRefreshed(true);
    onAnnounce?.('Google connection status refreshed');
    setTimeout(() => setRefreshed(false), 2500);
  };

  const handleDisconnect = async () => {
    setDisconnecting(true);
    setDisconnectError(null);
    try {
      const token = authToken || localStorage.getItem('auth_token');
      const response = await fetch(`${config.apiUrl}/api/auth/google/revoke`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
      if (!response.ok) throw new Error('Could not disconnect Google. Try again.');
      window.dispatchEvent(new CustomEvent('googleAuthSuccess'));
      setConfirmOpen(false);
      await checkStatus();
      onAnnounce?.('Google disconnected');
    } catch (err) {
      setDisconnectError(err instanceof Error ? err.message : 'Could not disconnect Google. Try again.');
    } finally {
      setDisconnecting(false);
    }
  };

  const connected = googleEmail !== null;
  const reconnectButton = <GoogleAuthButton onAuthSuccess={checkStatus} variant="secondary" label="Reconnect" />;

  return (
    <SectionCard title="Connected accounts">
      {initialLoad ? (
        <Box aria-busy="true">
          <Skeleton variant="rounded" height={40} sx={{ mb: 1.5 }} />
          <Skeleton variant="rounded" height={32} sx={{ mb: 1 }} />
          <Skeleton variant="rounded" height={32} />
        </Box>
      ) : !connected ? (
        <Box>
          <Typography sx={{ fontSize: '15px', color: t.muted, mb: 2 }}>
            Connect Google to sync expiry reminders with your calendar and detect renewal certificates arriving in your inbox.
          </Typography>
          <GoogleAuthButton onAuthSuccess={checkStatus} variant={allowPrimary ? 'brand' : 'secondary'} label="Connect Google" />
        </Box>
      ) : (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            <Box sx={{ width: 44, height: 44, borderRadius: '10px', bgcolor: t.surfaceSoft, color: t.ink, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, flexShrink: 0 }} aria-hidden>
              G
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontWeight: 600, fontSize: '17px', color: t.ink }}>Google</Typography>
              <Typography noWrap title={googleEmail || undefined} sx={{ fontSize: '14px', color: t.muted }}>{googleEmail}</Typography>
            </Box>
            <Button
              size="small"
              variant="outlined"
              onClick={handleRefresh}
              disabled={refreshing}
              aria-busy={refreshing}
              startIcon={refreshing ? <CircularProgress size={14} /> : refreshed ? <CheckIcon sx={{ fontSize: 16 }} /> : <RefreshIcon sx={{ fontSize: 16 }} />}
              sx={{ textTransform: 'none', fontWeight: 500 }}
            >
              {refreshing ? 'Refreshing' : refreshed ? 'Updated' : 'Refresh'}
            </Button>
            <Button
              size="small"
              variant="outlined"
              color="error"
              onClick={() => { setDisconnectError(null); setConfirmOpen(true); }}
              sx={{ textTransform: 'none', fontWeight: 500 }}
            >
              Disconnect
            </Button>
          </Box>

          <Box sx={{ ml: 2.25, mt: 1, pl: 2, borderLeft: `1px solid ${t.hairline}` }}>
            <ServiceRow icon={<CalendarIcon sx={{ fontSize: 18 }} />} name="Calendar" desc="Sync expiry reminders automatically" status={calendar} reconnect={reconnectButton} />
            <ServiceRow icon={<EmailIcon sx={{ fontSize: 18 }} />} name="Gmail" desc="Auto-detect renewal certificates" status={gmail} reconnect={reconnectButton} />
          </Box>
        </>
      )}

      <Dialog open={confirmOpen} onClose={disconnecting ? undefined : () => setConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Disconnect Google?</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 1 }}>
            Calendar sync and automatic renewal detection from Gmail will stop. Your existing reminders and Google Calendar events will be deleted.
          </DialogContentText>
          {disconnectError && <Typography role="alert" sx={{ fontSize: '14px', color: t.error }}>{disconnectError}</Typography>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} disabled={disconnecting}>Cancel</Button>
          <Button
            onClick={handleDisconnect}
            disabled={disconnecting}
            color="error"
            variant="outlined"
            startIcon={disconnecting ? <CircularProgress size={14} /> : undefined}
          >
            Disconnect
          </Button>
        </DialogActions>
      </Dialog>
    </SectionCard>
  );
};

export default GoogleIntegrationCard;
